import React, { useState } from "react";
import { useDMUnreadCount } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useRealtimeDM } from "../hooks/useRealtimeDM";
import { useTranslation } from "../hooks/useTranslation";
import ContactModal from "./ContactModal";
import NotificationBell from "./NotificationBell";

/** Messages (Instagram-style paper plane) icon */
const DMIcon = ({ ariaLabel }: { ariaLabel: string }) => (
  <svg
    aria-label={ariaLabel}
    className="x1lliihq x1n2onr6 x5n08af"
    fill="currentColor"
    height="24"
    role="img"
    viewBox="0 0 24 24"
    width="24"
  >
    <title>{ariaLabel}</title>
    <path
      d="M13.973 20.046 21.77 6.928C22.8 5.195 21.55 3 19.535 3H4.466C2.138 3 .984 5.825 2.646 7.456l4.842 4.752 1.723 7.121c.548 2.266 3.571 2.721 4.762.717Z"
      fill="none"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <line
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      x1="7.488"
      x2="15.515"
      y1="12.208"
      y2="7.641"
    />
  </svg>
);

export type HeaderLoggedInActionsProps = {
  onNavigate: (path: string) => void;
  messagesPathActive: boolean;
};

/** Messages button, unread badge, notification bell, and contact modal — only mounted when user is logged in. */
const HeaderLoggedInActions: React.FC<HeaderLoggedInActionsProps> = ({
  onNavigate,
  messagesPathActive,
}) => {
  const { t } = useTranslation();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { user } = useAuth();
  useRealtimeDM();
  const { data: dmUnreadData } = useDMUnreadCount(user?.id ?? undefined);
  const dmUnread = dmUnreadData?.count ?? 0;

  return (
    <>
      <button
        onClick={() => onNavigate("/messages")}
        className={`relative p-2 me-2 rounded-md transition-colors ${
          messagesPathActive
            ? "text-blue-500 dark:text-blue-400"
            : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        } hover:bg-gray-100 dark:hover:bg-gray-800`}
        aria-label={t('header.messages')}
      >
        <div className="relative">
          <DMIcon ariaLabel={t('header.messages')} />
          {dmUnread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 border-2 border-white dark:border-gray-800">
              {dmUnread > 9 ? "9+" : dmUnread}
            </span>
          )}
        </div>
      </button>
      <NotificationBell className="me-2" />
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </>
  );
};

export default HeaderLoggedInActions;
